import React, { useState, useCallback, useMemo } from 'react';
import {
  ShieldCheck,
  Lock,
  Wifi,
  Users,
  Key,
  Eye,
  Settings,
  X,
  RefreshCw,
  Zap,
} from 'lucide-react';

// --- Utility Functions & Mock Data ---

// Mocking a professional logging/notification system (no 'alert()')
const useNotification = () => {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);

  const notify = (msg) => {
    setMessage(msg);
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  };
  
  return { notify, message, visible };
};

const MOCK_SESSIONS = [
  { id: 101, user: 'johndoe@corp.com', device: 'Chrome on Windows', ip: '203.0.113.45', location: 'New York, US', lastActive: '2 min ago', active: true },
  { id: 102, user: 'janedoe@corp.com', device: 'Safari on Mac', ip: '198.51.100.12', location: 'London, UK', lastActive: '1 hr ago', active: true },
  { id: 103, user: 'peter.pan@corp.com', device: 'Firefox on Linux', ip: '203.0.113.100', location: 'Berlin, DE', lastActive: '8 hrs ago', active: false },
];

const MOCK_IP_POLICIES = [
  { id: 201, ipRange: '192.168.1.0/24', type: 'Whitelist', description: 'Office Network 1' },
  { id: 202, ipRange: '45.23.10.15', type: 'Blacklist', description: 'Known Spam Source' },
];

const MOCK_INITIAL_CONFIG = {
  mfaEnabled: true,
  passwordMinLength: 12,
  passwordRequireSymbols: true,
  sessionTimeoutMinutes: 60,
  idleLogoutMinutes: 30,
};

// --- Reusable UI Components ---

const Panel = ({ title, icon: Icon, children }) => (
  <div className="bg-white p-4 sm:p-5 rounded-xl shadow-lg border border-gray-100 transition-shadow hover:shadow-xl">
    <h2 className="text-sm font-semibold text-gray-700 flex items-center mb-4 pb-2 border-b border-gray-100">
      <Icon className="w-4 h-4 mr-2 text-indigo-500" />
      {title}
    </h2>
    <div className="text-xs text-gray-600 space-y-3">
      {children}
    </div>
  </div>
);

const ButtonPrimary = ({ onClick, children, className = '' }) => (
  <button
    onClick={onClick}
    className={`px-2 py-1 text-xs font-medium rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 
                bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:bg-indigo-300 ${className}`}
  >
    {children}
  </button>
);

const Badge = ({ children, type = 'info' }) => {
  let color = 'bg-gray-100 text-gray-600';
  if (type === 'success') color = 'bg-green-100 text-green-700';
  if (type === 'danger') color = 'bg-red-100 text-red-700';

  return (
    <span className={`px-2 py-0.5 inline-flex text-[10px] leading-4 font-semibold rounded-full ${color}`}>
      {children}
    </span>
  );
};

// --- Feature Components ---

const SessionManagement = ({ sessions, notify, onRevoke }) => (
  <Panel title="Active User Sessions" icon={Users}>
    <p className='text-[11px] text-gray-500'>Review and revoke currently active sessions across all devices and locations.</p>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {['User', 'Device', 'IP Address', 'Location', 'Last Active', 'Status', 'Action'].map((header) => (
              <th key={header} className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sessions.map((session) => (
            <tr key={session.id} className={session.active ? '' : 'opacity-60'}>
              <td className="px-4 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{session.user}</td>
              <td className="px-4 py-2 whitespace-nowrap text-xs">{session.device}</td>
              <td className="px-4 py-2 whitespace-nowrap text-xs">{session.ip}</td>
              <td className="px-4 py-2 whitespace-nowrap text-xs">{session.location}</td>
              <td className="px-4 py-2 whitespace-nowrap text-xs">{session.lastActive}</td>
              <td className="px-4 py-2 whitespace-nowrap">
                <Badge type={session.active ? 'success' : 'danger'}>{session.active ? 'Active' : 'Expired'}</Badge>
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                {session.active ? (
                  <button
                    onClick={() => onRevoke(session.id)}
                    className="text-red-500 hover:text-red-700 transition-colors text-xs font-medium"
                    aria-label={`Revoke session for ${session.user}`}
                  >
                    Revoke
                  </button>
                ) : (
                  <span className="text-gray-400 text-xs">N/A</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Panel>
);

const MfaPolicy = ({ config, onSave, notify }) => {
  const [localMfa, setLocalMfa] = useState(config.mfaEnabled);

  const handleSave = () => {
    // Mock API call to save config
    onSave({ ...config, mfaEnabled: localMfa });
    notify(`MFA policy saved. Status: ${localMfa ? 'Enabled' : 'Disabled'}`);
  };

  return (
    <Panel title="Two-Factor Authentication (MFA) Policy" icon={Key}>
      <div className="flex justify-between items-center py-2 border-b border-gray-100">
        <div className='flex flex-col'>
            <span className="font-medium text-sm">MFA Enforcement</span>
            <span className='text-[11px] text-gray-500'>Require all users to use TOTP, SMS, or FIDO2.</span>
        </div>
        <label className="inline-flex relative items-center cursor-pointer">
          <input 
            type="checkbox" 
            checked={localMfa} 
            onChange={(e) => setLocalMfa(e.target.checked)} 
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-indigo-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
        </label>
      </div>
      <ButtonPrimary onClick={handleSave} className="w-full mt-3">
        <Lock className="w-3 h-3 mr-1" /> Save MFA Configuration
      </ButtonPrimary>
    </Panel>
  );
};

const PasswordPolicy = ({ config, onSave, notify }) => {
  const [localConfig, setLocalConfig] = useState(config);

  const handleChange = (field, value) => {
    setLocalConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Basic validation
    if (localConfig.passwordMinLength < 8) {
        return notify('Minimum length must be at least 8 characters.', 'danger');
    }
    // Mock API call to save config
    onSave(localConfig);
    notify('Password policy successfully updated.');
  };

  return (
    <Panel title="Password Policy Enforcement" icon={Lock}>
      <div className="space-y-3">
        {/* Min Length */}
        <div className="flex justify-between items-center">
          <label htmlFor="minLength" className="font-medium text-sm">Minimum Length</label>
          <input
            id="minLength"
            type="number"
            min="8"
            value={localConfig.passwordMinLength}
            onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value) || 0)}
            className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs"
          />
        </div>

        {/* Require Symbols */}
        <div className="flex justify-between items-center">
          <label className="font-medium text-sm">Require Symbols/Special Chars</label>
          <label className="inline-flex relative items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={localConfig.passwordRequireSymbols} 
              onChange={(e) => handleChange('passwordRequireSymbols', e.target.checked)} 
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-indigo-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
        </div>

        {/* Session Timeout */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
          <label htmlFor="sessionTimeout" className="font-medium text-sm">Session Timeout (min)</label>
          <input
            id="sessionTimeout"
            type="number"
            min="5"
            value={localConfig.sessionTimeoutMinutes}
            onChange={(e) => handleChange('sessionTimeoutMinutes', parseInt(e.target.value) || 0)}
            className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs"
          />
        </div>

         {/* Idle Logout */}
         <div className="flex justify-between items-center">
          <label htmlFor="idleLogout" className="font-medium text-sm">Idle Logout (min)</label>
          <input
            id="idleLogout"
            type="number"
            min="5"
            value={localConfig.idleLogoutMinutes}
            onChange={(e) => handleChange('idleLogoutMinutes', parseInt(e.target.value) || 0)}
            className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs"
          />
        </div>
      </div>

      <ButtonPrimary onClick={handleSave} className="w-full mt-3">
        <Settings className="w-3 h-3 mr-1" /> Update Password & Session Settings
      </ButtonPrimary>
    </Panel>
  );
};

const IpAccessControl = ({ policies, notify, onAdd, onRemove }) => {
  const [ipAddress, setIpAddress] = useState('');
  const [policyType, setPolicyType] = useState('Whitelist');
  const [description, setDescription] = useState('');

  const handleAddPolicy = () => {
    if (!ipAddress.trim()) return notify('IP Address cannot be empty.');
    
    // Mock validation to ensure it looks like an IP or CIDR (simplistic)
    if (!ipAddress.includes('.') && !ipAddress.includes('/')) return notify('Invalid IP/CIDR format.');

    const newPolicy = {
      id: Date.now(),
      ipRange: ipAddress.trim(),
      type: policyType,
      description: description.trim() || `${policyType}ed Range`,
    };

    onAdd(newPolicy);
    notify(`New ${policyType} policy added for ${ipAddress}.`);
    setIpAddress('');
    setDescription('');
  };

  return (
    <Panel title="IP Whitelisting & Blacklisting" icon={Wifi}>
      <p className='text-[11px] text-gray-500'>Control network access by specifying allowed (Whitelist) or blocked (Blacklist) IP addresses or CIDR ranges.</p>
      
      {/* Policy List */}
      <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
        <h4 className="text-[11px] font-semibold text-gray-700 mb-1">Current Policies</h4>
        {policies.map((p) => (
          <div key={p.id} className="flex justify-between items-center p-1.5 rounded-md hover:bg-white transition-colors">
            <div className='flex flex-col'>
                <span className="font-medium text-xs text-gray-800">{p.ipRange}</span>
                <span className='text-[10px] text-gray-500'>{p.description}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge type={p.type === 'Whitelist' ? 'success' : 'danger'}>{p.type}</Badge>
              <button
                onClick={() => onRemove(p.id)}
                className="text-gray-400 hover:text-red-500 transition-colors"
                aria-label={`Remove IP policy ${p.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add New Policy Form */}
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
        <h4 className="text-[11px] font-semibold text-gray-700">Add New Policy</h4>
        <input
          type="text"
          placeholder="IP Address or CIDR (e.g., 10.0.0.1 or 10.0.0.0/24)"
          value={ipAddress}
          onChange={(e) => setIpAddress(e.target.value)}
          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-xs"
        />
        <input
          type="text"
          placeholder="Description (e.g., Remote Office VPN)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-xs"
        />
        <select
          value={policyType}
          onChange={(e) => setPolicyType(e.target.value)}
          className="w-full px-2 py-1 border border-gray-300 rounded-lg text-xs appearance-none bg-white"
        >
          <option value="Whitelist">Whitelist (Allow Access)</option>
          <option value="Blacklist">Blacklist (Deny Access)</option>
        </select>
        <ButtonPrimary onClick={handleAddPolicy} className="w-full mt-2">
          <Zap className="w-3 h-3 mr-1" /> Enforce New Policy
        </ButtonPrimary>
      </div>
    </Panel>
  );
};

// --- Main Component ---

export default function SecurityAccessControl() {
  const [activeSessions, setActiveSessions] = useState(MOCK_SESSIONS);
  const [ipPolicies, setIpPolicies] = useState(MOCK_IP_POLICIES);
  const [securityConfig, setSecurityConfig] = useState(MOCK_INITIAL_CONFIG);

  const { notify, message, visible } = useNotification();

  // Handlers for Session Management
  const handleRevokeSession = useCallback((sessionId) => {
    setActiveSessions(prev => 
      prev.map(s => s.id === sessionId ? { ...s, active: false } : s)
    );
    notify(`Session ${sessionId} revoked successfully.`);
  }, [notify]);

  // Handlers for IP Policies
  const handleAddPolicy = useCallback((newPolicy) => {
    setIpPolicies(prev => [...prev, newPolicy]);
  }, []);

  const handleRemovePolicy = useCallback((policyId) => {
    setIpPolicies(prev => prev.filter(p => p.id !== policyId));
    notify(`IP policy ${policyId} removed.`);
  }, [notify]);

  // Handler for Policy Configurations
  const handleSaveConfig = useCallback((newConfig) => {
    setSecurityConfig(newConfig);
    // Notification handled in child components
  }, []);
  
  // Custom Styles to enforce professional aesthetic (small font, specific faces)
  // NOTE: Roboto/Poppins must be linked externally in your environment's <head>.
  const pageStyle = {
    fontFamily: '"Roboto", "Poppins", sans-serif',
  };

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen" style={pageStyle}>
      {/* HEADER */}
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-xl font-bold text-gray-800 flex items-center">
          <ShieldCheck className="w-6 h-6 mr-3 text-indigo-600" />
          Security & Access Control
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage core application security policies, monitor active users, and configure multi-factor authentication.
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (Policies) */}
        <div className="lg:col-span-1 space-y-6">
          <MfaPolicy 
            config={securityConfig} 
            onSave={handleSaveConfig} 
            notify={notify} 
          />
          <PasswordPolicy 
            config={securityConfig} 
            onSave={handleSaveConfig} 
            notify={notify} 
          />
        </div>

        {/* Center/Right Column (IP and Sessions) */}
        <div className="lg:col-span-2 space-y-6">
          <SessionManagement 
            sessions={activeSessions} 
            notify={notify} 
            onRevoke={handleRevokeSession} 
          />
          <IpAccessControl
            policies={ipPolicies}
            notify={notify}
            onAdd={handleAddPolicy}
            onRemove={handleRemovePolicy}
          />
        </div>
      </div>

      {/* Custom Notification Box (Instead of alert()) */}
      <div 
        className={`fixed bottom-4 right-4 p-3 rounded-lg shadow-xl text-xs font-medium transition-all duration-300 
                    ${visible ? 'translate-y-0 opacity-100 bg-gray-800 text-white' : 'translate-y-4 opacity-0'}`}
      >
        <Key className='w-4 h-4 mr-2 inline' />
        {message}
      </div>
    </div>
  );
}